import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'tech-discussion');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '分类';
  const categoryArticles = articles.filter((a) => a.categorySlug === activeSlug);

  useSEO({
    title: currentCategory ? `${currentCategory.name} - 一品楼论坛 | ${currentCategory.description}` : '分类 - 一品楼论坛',
    description: currentCategory?.description || '一品楼论坛分类板块，浏览精彩内容',
    canonicalUrl: `https://wouzfuo.cn/category/${activeSlug}`,
  });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://wouzfuo.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://wouzfuo.cn/category/${activeSlug}`,
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 一品楼论坛`,
    description: currentCategory?.description || '',
    url: `https://wouzfuo.cn/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '一品楼论坛',
      url: 'https://wouzfuo.cn',
    },
  };

  return (
    <>
      <SchemaOrg data={webpageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100/70 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb items={breadcrumbItems} />
              <div className="flex items-center gap-3 mt-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className={`${currentCategory?.icon || 'ri-folder-line'} text-lg`}></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-0.5">
                    {currentCategory?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              {categoryArticles.length > 0 ? (
                categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="default" />
                ))
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无帖子</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}